/* eslint max-len: ["error", { "code": 120 }] */
import { graphql } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import React, { useCallback, useEffect, useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { getJWTTier } from '@src/utils';

// eslint-disable-next-line import/no-unresolved
import { getTieringLaunchFlag } from '../utils/tiering';
import {
  addErrorInDatadogRum,
  deleteLocalStorage,
  getLocalStorage,
  isBrowser,
  refreshTokenWithRetry,
} from '../utils/utilities';

import { TIERS } from '../../../../src/constants/tiering';
import Layout from '../components/common/layout';
import Success from '../components/signup/success';
import useGetCountryCode from '../hooks/useGetCountryCode';

const SuccessTemplate = ({ data, location, pageContext }) => {
  const { prismicSignUpLayout, prismicSignUpSuccessPage } = data;
  const { lang } = pageContext;
  const { countryCode } = useGetCountryCode();
  const { rtTierProductCountries, rtJwtEntitlement } = useFlags();

  const selectedPlan = JSON.parse(getLocalStorage('selected-plan') || 'null');
  const newSubscription = JSON.parse(getLocalStorage('new-subscription') || 'null');
  const userInfo = JSON.parse(getLocalStorage('user-info') || 'null');

  const [isTokenRefreshing, setIsTokenRefreshing] = useState(true);
  const [tokenRefreshFailed, setTokenRefreshFailed] = useState(false);

  const clearLocalStroage = useCallback(() => {
    // we can't delete new-subscription here in 500ms because it is used in the Success component
    // deleteLocalStorage('new-subscription');
    deleteLocalStorage('planId');
    deleteLocalStorage('selected-plan');
    deleteLocalStorage('isFromBAU');
  }, []);

  useEffect(() => {
    // delete local storage after 500ms
    const timer = setTimeout(() => clearLocalStroage(), 500);
    return () => clearTimeout(timer);
  }, [clearLocalStroage]);

  // Automatically refresh JWT token on page load to update entitlements
  // Retry every 1 second if LMOD entitlement is not present, up to 30 attempts
  useEffect(() => {
    const refreshTokenOnLoad = async () => {
      if (isBrowser) {
        try {
          await refreshTokenWithRetry();
        } catch (error) {
          addErrorInDatadogRum(error);
          setTokenRefreshFailed(true);
        } finally {
          setIsTokenRefreshing(false);
        }
      }
    };
    refreshTokenOnLoad();
  }, []);

  const handleRetryRefresh = async () => {
    setTokenRefreshFailed(false);
    setIsTokenRefreshing(true);
    try {
      await refreshTokenWithRetry();
    } catch (error) {
      addErrorInDatadogRum(error);
      setTokenRefreshFailed(true);
    } finally {
      setIsTokenRefreshing(false);
    }
  };

  const handleStarted = async () => {
    if (isBrowser) {
      // Refresh JWT token if tiering feature is enabled
      const isTieringEnable = getTieringLaunchFlag(countryCode, rtTierProductCountries, true);
      if (isTieringEnable) {
        try {
          const jwtToken = await refreshTokenWithRetry();
          const tier = getJWTTier(jwtToken, rtJwtEntitlement);
          const redirectionUrl = tier !== TIERS.BASE ? '/survey/start' : '/explore';
          window.location.href = redirectionUrl;
        } catch (error) {
          addErrorInDatadogRum(error);
          setTokenRefreshFailed(true);
        }
      } else {
        const redirectionUrl = '/survey/start';
        window.location.href = redirectionUrl;
      }
    }
  };

  return (
    <Layout
      data={prismicSignUpLayout.data}
      contentWidth={580}
      omitClose
      lang={lang}
      pathName="/signup/success"
      title="Sign Up - Success"
    >
      <Success
        data={prismicSignUpSuccessPage.data}
        handleStarted={handleStarted}
        handleRetryRefresh={handleRetryRefresh}
        prismicSignUpLayout={prismicSignUpLayout}
        pageContext={pageContext}
        location={location}
        selectedPlan={selectedPlan}
        newSubscription={newSubscription}
        userInfo={userInfo}
        isTokenRefreshing={isTokenRefreshing}
        tokenRefreshFailed={tokenRefreshFailed}
      />
    </Layout>
  );
};
export default withPrismicPreview(SuccessTemplate);

export const query = graphql`
  query ($lang: String!) {
    prismicSignUpSuccessPage(lang: { eq: $lang }) {
      _previewable
      data {
        page_title
        description
        get_started
        dont_refresh
        welcome_aboard
        alert_title {
          text
        }
        alert_description {
          richText
        }
        title {
          text
        }
      }
    }
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
      }
    }
  }
`;
